'use client';

import { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { usePolling } from '@/hooks/usePolling';

interface UserLocationContextType {
  userCoords: { latitude: number; longitude: number } | null;
  isGeolocationAvailable: boolean;
  isCheckingLocation: boolean;
  requestLocation: () => void;
}

const UserLocationContext = createContext<UserLocationContextType | undefined>(undefined);

export function UserLocationProvider({ children }: { children: ReactNode }) {
  const [userCoords, setUserCoords] = useState<{ latitude: number; longitude: number } | null>(null);
  const [shouldPollLocation, setShouldPollLocation] = useState(false);
  const [isCheckingLocation, setIsCheckingLocation] = useState(true);
  const isGeolocationAvailable = typeof navigator !== 'undefined' && 'geolocation' in navigator;

  // Check for existing location permissions on mount
  useEffect(() => {
    if (!isGeolocationAvailable) {
      setIsCheckingLocation(false);
      return;
    }

    // Check if we can get location immediately (user already granted permission)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserCoords({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        setIsCheckingLocation(false);
        setShouldPollLocation(true);
      },
      (error) => {
        // Only show the location required screen after we've confirmed permissions are denied
        setIsCheckingLocation(false);
      },
      { timeout: 5000, maximumAge: 60000 }
    );
  }, [isGeolocationAvailable]);

  usePolling({
    key: 'user-location',
    fetchFn: async () => {
      if (!isGeolocationAvailable) return null;
      console.log('Polling for user location');
      return await new Promise<{ latitude: number; longitude: number } | null>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) => resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude

          }),
          (error) => resolve(null)
        );
      });
    },
    interval: 30000,
    enabled: shouldPollLocation,
    onSuccess: (coords) => {
      console.log('User location fetched', coords);
      if (coords) setUserCoords(coords);
    },
    showToast: false,
  });

  const requestLocation = () => {
    setShouldPollLocation(true);
  };

  return (
    <UserLocationContext.Provider value={{ userCoords, isGeolocationAvailable, isCheckingLocation, requestLocation }}>
      {children}
    </UserLocationContext.Provider>
  );
}

export function useUserLocation() {
  const context = useContext(UserLocationContext);
  if (!context) throw new Error('useUserLocation must be used within a UserLocationProvider');
  return context;
} 